import React from 'react';
import {
  CHANNEL_LABELS,
  CHANNELS,
  Platform,
  ReaderVersionFiltersState as Filters,
  PLATFORMS
} from '../types';

interface ReaderVersionFiltersProps {
  filters: Filters;
  onFiltersChange: (filters: Partial<Filters>) => void;
  onClearFilters: () => void;
}

const ReaderVersionFilters: React.FC<ReaderVersionFiltersProps> = ({
  filters,
  onFiltersChange,
  onClearFilters
}) => {
  const handleFilterChange = (field: keyof Filters, value: string) => {
    // Kênh của nền tảng cũ không tồn tại ở nền tảng mới, và để sót lại thì ô
    // lọc kênh biến mất mà bộ lọc vẫn đang chặn kết quả
    if (field === 'platform') {
      onFiltersChange({ platform: value, channel: '' });
      return;
    }
    onFiltersChange({ [field]: value });
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  // API chỉ lọc kênh trong phạm vi một nền tảng, nên chưa chọn nền tảng thì
  // chưa có kênh nào để chọn
  const channels = CHANNELS[filters.platform as Platform] ?? [];

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
      <div className="flex flex-wrap items-center gap-4">
        {/* Platform Filter */}
        <div className="flex-1 min-w-48">
          <label className="block text-sm font-medium text-gray-700 mb-1">Nền tảng</label>
          <select
            value={filters.platform}
            onChange={(e) => handleFilterChange("platform", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          >
            <option value="">Tất cả nền tảng</option>
            {PLATFORMS.map((platform) => (
              <option key={platform.value} value={platform.value}>
                {platform.label}
              </option>
            ))}
          </select>
        </div>

        {/* Channel Filter — chỉ có nghĩa khi nền tảng đó phát nhiều kênh */}
        {channels.length > 1 && (
          <div className="flex-1 min-w-40">
            <label className="block text-sm font-medium text-gray-700 mb-1">Kênh</label>
            <select
              value={filters.channel}
              onChange={(e) => handleFilterChange("channel", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="">Tất cả kênh</option>
              {channels.map((channel) => (
                <option key={channel} value={channel}>
                  {CHANNEL_LABELS[channel]}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Mandatory Filter */}
        <div className="flex-1 min-w-40">
          <label className="block text-sm font-medium text-gray-700 mb-1">Bắt buộc</label>
          <select
            value={filters.mandatory}
            onChange={(e) => handleFilterChange("mandatory", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          >
            <option value="">Tất cả</option>
            <option value="true">Bắt buộc</option>
            <option value="false">Tùy chọn</option>
          </select>
        </div>

        {/* Active Filter */}
        <div className="flex-1 min-w-40">
          <label className="block text-sm font-medium text-gray-700 mb-1">Trạng thái</label>
          <select
            value={filters.active}
            onChange={(e) => handleFilterChange("active", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          >
            <option value="">Tất cả</option>
            <option value="true">Kích hoạt</option>
            <option value="false">Không kích hoạt</option>
          </select>
        </div>

        {/* Version Filter */}
        <div className="flex-1 min-w-40">
          <label className="block text-sm font-medium text-gray-700 mb-1">Phiên bản</label>
          <input
            type="text"
            value={filters.version}
            onChange={(e) => handleFilterChange("version", e.target.value)}
            placeholder="Tìm kiếm phiên bản..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
        </div>

        {/* Date Range Filters */}
        <div className="flex-1 min-w-40">
          <label className="block text-sm font-medium text-gray-700 mb-1">Từ ngày</label>
          <input
            type="date"
            value={filters.from_date}
            onChange={(e) => handleFilterChange("from_date", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
        </div>

        <div className="flex-1 min-w-40">
          <label className="block text-sm font-medium text-gray-700 mb-1">Đến ngày</label>
          <input
            type="date"
            value={filters.to_date}
            onChange={(e) => handleFilterChange("to_date", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
        </div>

        {/* Clear Filters Button */}
        <div className="flex items-end">
          <button
            onClick={onClearFilters}
            disabled={!hasActiveFilters}
            className={`mt-6 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              hasActiveFilters
                ? "text-gray-700 bg-gray-100 border border-gray-300 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                : "text-gray-400 bg-gray-50 border border-gray-200 cursor-not-allowed"
            }`}
          >
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              Xóa bộ lọc
            </div>
          </button>
        </div>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="flex flex-wrap gap-2">
            <span className="text-sm text-gray-600">Bộ lọc đang áp dụng:</span>

            {filters.platform && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Nền tảng: {PLATFORMS.find((p) => p.value === filters.platform)?.label}
                <button
                  onClick={() => handleFilterChange("platform", "")}
                  className="ml-1 text-blue-600 hover:text-blue-800"
                >
                  ×
                </button>
              </span>
            )}

            {filters.mandatory && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {filters.mandatory === "true" ? "Bắt buộc" : "Tùy chọn"}
                <button
                  onClick={() => handleFilterChange("mandatory", "")}
                  className="ml-1 text-green-600 hover:text-green-800"
                >
                  ×
                </button>
              </span>
            )}

            {filters.active && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                {filters.active === "true" ? "Kích hoạt" : "Không kích hoạt"}
                <button
                  onClick={() => handleFilterChange("active", "")}
                  className="ml-1 text-indigo-600 hover:text-indigo-800"
                >
                  ×
                </button>
              </span>
            )}

            {filters.version && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Phiên bản: {filters.version}
                <button
                  onClick={() => handleFilterChange("version", "")}
                  className="ml-1 text-purple-600 hover:text-purple-800"
                >
                  ×
                </button>
              </span>
            )}

            {filters.from_date && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                Từ: {filters.from_date}
                <button
                  onClick={() => handleFilterChange("from_date", "")}
                  className="ml-1 text-yellow-600 hover:text-yellow-800"
                >
                  ×
                </button>
              </span>
            )}

            {filters.to_date && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                Đến: {filters.to_date}
                <button
                  onClick={() => handleFilterChange("to_date", "")}
                  className="ml-1 text-orange-600 hover:text-orange-800"
                >
                  ×
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReaderVersionFilters;
